import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js'
import * as _collectors from '@debbie/common/dist/collectors/index.js'
import { Plan } from '@debbie/common/dist/economy/payment-plans/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as paymentPlansAPI from '@debbie/cortex/dist/api/payment-plans.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { h } from '@debbie/cortex/dist/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import AlertCircle from '@debbie/iris/dist/components/Icons/AlertCircle.js'
import ArrowRight from '@debbie/iris/dist/components/Icons/ArrowRight.js'
import CreditCard from '@debbie/iris/dist/components/Icons/CreditCard.js'
import Lock from '@debbie/iris/dist/components/Icons/Lock.js'
import { Button, Panel, Well } from '@debbie/iris/dist/components/index.js'
import BankTransfer from '@debbie/iris/dist/components/Pay/PaymentOption/BankTransfer.js'
import FI from '@debbie/iris/dist/components/Pay/PaymentOption/FI.js'
import { CardIcons } from '@debbie/iris/dist/components/Pay/PaymentOption/PaymentPortal.js'
import Swift from '@debbie/iris/dist/components/Pay/PaymentOption/Swift.js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import EntriesList from './EntriesList.js'

interface Props {
  singleCase: any
  paymentPlan: Plan
}

interface State {
  cvc: string
  cardNumber: string
  expiry: string
  connectCardError: null | string
}

const MANUAL_TRANSFER_TYPES: _collectors.PaymentOptionType[] = [
  _collectors.PaymentOptionType.BANKTRANSFER,
  _collectors.PaymentOptionType.FI,
  _collectors.PaymentOptionType.SWIFT,
]

const ConnectCardStep = (props: Props) => {
  const params = useParams<{ id: string }>()
  const { id } = params
  const [state, setState] = useState<State>({
    cvc: '',
    cardNumber: '',
    expiry: '',
    connectCardError: null,
  })

  const lang = useSelector((s: RootState) => s.lang)
  const authToken = useSelector((s: RootState) => s.authToken)

  const { singleCase, paymentPlan } = props

  const connectCard = useMutation(
    async (cardOption: _collectors.PaymentOption) => {
      const link = await paymentPlansAPI.getPSPLink(
        paymentPlan.paymentPlanId,
        _collectors.PaymentOptionTypeToProviderMap[cardOption.type],
        cardOption.id,
        window.location.href,
        window.location.href,
        false,
      )
      location.replace(link.url)
    },
    {
      onError: (error) => {
        console.error(error)
        alert('Error. Please try again later.')
      },
    },
  )

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const collectorLoadable = useCollector(
    (singleCase &&
      creditor &&
      getCollectorOnCase(
        singleCase.type,
        singleCase.collectorId,
        creditor.identityCollectorId,
        creditor.defaultCollectorId,
      )) ||
      undefined,
  )
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  const paymentOptions: _collectors.PaymentOption[] | null =
    (collector &&
      singleCase &&
      _collectors
        .getPaymentOptions(collector.paymentOptions, singleCase.currency)
        .filter((p) => !p.hideOnDebtorweb)) ||
    null

  const cardOption =
    paymentOptions &&
    paymentOptions.find(
      (paymentOption) =>
        paymentOption.isActive &&
        _collectors.PaymentOptionTypeToProviderMap[paymentOption.type],
    )

  const manualOptions =
    paymentOptions?.filter((paymentOption) =>
      MANUAL_TRANSFER_TYPES.includes(paymentOption.type),
    ) ?? []

  const renderManualOption = (paymentOption: _collectors.PaymentOption) => {
    switch (paymentOption.type) {
      case _collectors.PaymentOptionType.BANKTRANSFER:
        return collector ? (
          <BankTransfer
            paymentOption={paymentOption}
            case={singleCase}
            collector={collector}
          />
        ) : null
      case _collectors.PaymentOptionType.FI:
        return <FI paymentOption={paymentOption} case={singleCase} />
      case _collectors.PaymentOptionType.SWIFT:
        return <Swift paymentOption={paymentOption} />
      default:
        return null
    }
  }

  const hasCardOption = !!cardOption
  const hasManualOptions = manualOptions.length > 0

  return (
    <div>
      {(hasCardOption || hasManualOptions) && (
        <Panel>
          <PaymentMethods>
            {hasCardOption && cardOption && collector && (
              <CardConnectContent>
                <CardIconWrap aria-hidden>
                  <CreditCard />
                </CardIconWrap>

                <CardHeading>
                  {lang.debtorWebPaymentPlans.automaticPaymentMethod}
                </CardHeading>
                <CardDescription>
                  {lang.debtorWebPaymentPlans.paymentMethodText}
                </CardDescription>

                <CardBrandsRow>
                  <CardIcons paymentOption={cardOption} />
                </CardBrandsRow>

                {state.connectCardError &&
                  typeof state.connectCardError === 'string' && (
                    <ErrorWell>
                      <Well>
                        <AlertCircle /> {state.connectCardError}
                      </Well>
                    </ErrorWell>
                  )}

                {singleCase && h.isLoaded(authToken) && (
                  <CtaWrap>
                    <Button
                      block
                      onClick={() => connectCard.mutate(cardOption)}
                      loading={connectCard.isLoading}
                    >
                      <CtaInner>
                        <span>{lang.debtorWebPaymentPlans.connectCard}</span>
                        <ArrowRight />
                      </CtaInner>
                    </Button>
                  </CtaWrap>
                )}

                <SecureHint>
                  <Lock />
                  <span>{lang.debtorWebPaymentPlans.securePaymentHint}</span>
                </SecureHint>
              </CardConnectContent>
            )}

            {hasCardOption && hasManualOptions && (
              <Divider>
                <span>{lang.debtorWebHome.or}</span>
              </Divider>
            )}

            {hasManualOptions && (
              <ManualSection>
                <ManualHeading>
                  {lang.debtorWebPaymentPlans.manualBankTransferTitle}
                </ManualHeading>
                <ManualHint>
                  {lang.debtorWebPaymentPlans.manualBankTransferHint}
                </ManualHint>
                <ManualOptionsList>
                  {manualOptions.map((paymentOption, idx) => (
                    <ManualOptionCard key={paymentOption.id || idx}>
                      {renderManualOption(paymentOption)}
                    </ManualOptionCard>
                  ))}
                </ManualOptionsList>
              </ManualSection>
            )}
          </PaymentMethods>
        </Panel>
      )}
      <Panel>
        <EntriesList paymentPlan={paymentPlan} currency={singleCase.currency} />
      </Panel>
    </div>
  )
}

export default ConnectCardStep

const PaymentMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px;
`

const CardConnectContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 8px 4px 4px;
  text-align: center;
`

const CardIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  color: #333;

  svg {
    width: 28px;
    height: 28px;
  }
`

const CardHeading = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
`

const CardDescription = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: 14px;
  line-height: 1.55;
  color: #555;
`

const CardBrandsRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 2px;
  flex-wrap: wrap;
`

const ErrorWell = styled.div`
  width: 100%;
  max-width: 480px;
`

const CtaWrap = styled.div`
  width: 100%;
  max-width: 380px;
  margin-top: 4px;
`

const CtaInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.15s ease;
  }

  button:hover & svg {
    transform: translateX(3px);
  }
`

const SecureHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #888;
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #eee;
  }
`

const ManualSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ManualHeading = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111;
`

const ManualHint = styled.p`
  margin: 0 0 4px 0;
  font-size: 13px;
  line-height: 1.5;
  color: #666;
`

const ManualOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ManualOptionCard = styled.div`
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 10px;

  /* Tighten the inner components which were styled for a busier panel layout. */
  h2 {
    margin: 0 0 6px 0;
    font-size: 15px;
    font-weight: 700;
    color: #111;
  }

  > div > p {
    margin: 0 0 10px 0;
    font-size: 13px;
    color: #666;
    line-height: 1.5;
  }

  ul {
    margin: 0;
  }
`
